import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { User, Mail, Building2, CreditCard, AlertTriangle, Loader2, ArrowUpRight, Clock, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceledAtPeriodEnd, setCanceledAtPeriodEnd] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [subDetails, setSubDetails] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadSubDetails = async () => {
      try {
        const { data } = await api.get('/stripe/subscription-details');
        setSubDetails(data);
      } catch (err) {
        console.error('Failed to load subscription details:', err);
      }
    };

    const loadCancelState = async () => {
      try {
        const { data: company } = await api.get(`/companies/${user.company_id}`);
        setCanceledAtPeriodEnd(!!company.subscription_cancel_at_period_end);
      } catch (err) {
        console.error('Failed to load subscription status:', err);
      }
    };
    
    const checkTrialDays = async () => {
      if (user?.plan_tier === 'limited_demo') {
        try {
          const { data: company } = await api.get(`/companies/${user.company_id}`);
          if (company.trial_expires_at) {
            const expiresAt = new Date(company.trial_expires_at);
            const now = new Date();
            const days = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
            setDaysRemaining(days);
          }
        } catch (err) {
          console.error('Failed to load trial info:', err);
        }
      }
    };
    
    if (user?.subscription_status === 'active') {
      loadSubDetails();
      checkTrialDays();
      if (user?.plan_tier !== 'limited_demo') {
        loadCancelState();
      }
    }
  }, [user]);

  const handleManageBilling = async () => {
    setLoadingPortal(true);
    try {
      const { data } = await api.post('/stripe/customer-portal');
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Failed to open billing portal');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open billing portal');
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelOpen(false);
    setCanceling(true);
    try {
      await api.post('/stripe/cancel-subscription');
      setCanceledAtPeriodEnd(true);
      toast.success('Subscription set to cancel. Your account stays active until the end of the billing period.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    try {
      await api.post('/stripe/reactivate-subscription');
      setCanceledAtPeriodEnd(false);
      toast.success('Subscription reactivated. It will renew normally.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setReactivating(false);
    }
  };

  const handleUpgradeToEnterprise = async () => {
    setUpgrading(true);
    try {
      const { data } = await api.post('/stripe/upgrade-to-enterprise', { origin_url: window.location.origin });
      if (data.url) {
        window.location.href = data.url; // Redirect to Stripe checkout
      } else {
        toast.success('Upgraded to Enterprise! Reloading...');
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start upgrade. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const handleUpgradeFromDemo = async (planId) => {
    setUpgrading(true);
    try {
      const originUrl = window.location.origin;
      const { data } = await api.post('/stripe/create-checkout-session', { 
        plan_id: planId, 
        origin_url: originUrl 
      });
      if (data.url) {
        window.location.href = data.url; // Redirect to Stripe checkout
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setUpgrading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div data-testid="profile-page" className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Profile</h1>
        <p className="text-sm text-muted-foreground">Manage your account, subscription, and payment methods</p>
      </div>

      {/* Account Information */}
      <Card className="rounded-xl shadow-[var(--shadow-soft)]">
        <CardHeader>
          <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <User className="inline mr-2 h-4 w-4" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Name</Label>
              <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <p className="text-sm font-medium">{user.email}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Role</Label>
              <Badge variant="secondary" className="text-xs capitalize">{user.role}</Badge>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Company</Label>
              <p className="text-sm font-medium flex items-center gap-2">
                <Building2 className="h-3 w-3 text-muted-foreground" />
                Company
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription & Billing */}
      <Card className="rounded-xl shadow-[var(--shadow-soft)]">
        <CardHeader>
          <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <CreditCard className="inline mr-2 h-4 w-4" />
            Subscription & Billing
          </CardTitle>
          <CardDescription>Manage your payment method and subscription</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Plan</Label>
              <p className="text-sm font-medium capitalize">{user.plan_tier || 'None'}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Badge variant={user.subscription_status === 'active' ? 'default' : 'secondary'} className="text-xs capitalize">
                {user.subscription_status || 'Inactive'}
              </Badge>
            </div>
            {subDetails?.current_period_end && (
              <div className="sm:col-span-2">
                <Label className="text-xs text-muted-foreground">Next Renewal Date</Label>
                <p className="text-sm font-medium">
                  {new Date(subDetails.current_period_end * 1000).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            )}
          </div>

          <Separator />


          {/* Limited Demo - Upgrade Options */}
          {user.plan_tier === 'limited_demo' && (
            <>
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-lg">Upgrade Your Trial</h3>
                </div>
                {daysRemaining !== null && (
                  <p className="text-sm text-muted-foreground mb-4">
                    {daysRemaining > 0 
                      ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining in your free trial`
                      : 'Your trial has expired'
                    }
                  </p>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="border rounded-lg p-4 hover:border-primary/40 transition-colors">
                    <p className="font-semibold text-sm mb-1">Professional</p>
                    <p className="text-2xl font-bold mb-2">
                      $249<span className="text-sm font-normal text-muted-foreground">/mo</span>
                    </p>
                    <ul className="text-xs space-y-1.5 text-muted-foreground mb-3">
                      <li className="flex items-start gap-1.5">
                        <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                        <span>Up to 5 properties</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                        <span>Unlimited vendors</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                        <span>$4.50 per vendor/month</span>
                      </li>
                    </ul>
                    <Button 
                      className="w-full" 
                      variant="outline"
                      onClick={() => handleUpgradeFromDemo('professional')} 
                      disabled={upgrading}
                      size="sm"
                    >
                      {upgrading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
                      Upgrade to Professional
                    </Button>
                  </div>
                  <div className="border-2 border-primary rounded-lg p-4 bg-primary/5 relative">
                    <Badge className="absolute -top-2.5 left-4 text-xs">Best Value</Badge>
                    <p className="font-semibold text-sm mb-1 mt-1">Enterprise</p>
                    <p className="text-2xl font-bold mb-2">
                      $599<span className="text-sm font-normal text-muted-foreground">/mo</span>
                    </p>
                    <ul className="text-xs space-y-1.5 text-muted-foreground mb-3">
                      <li className="flex items-start gap-1.5">
                        <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                        <span>Unlimited properties</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                        <span>$3.50 per vendor/month</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                        <span>Vendor-Paid billing option</span>
                      </li>
                    </ul>
                    <Button 
                      className="w-full" 
                      onClick={() => handleUpgradeFromDemo('enterprise')} 
                      disabled={upgrading}
                      size="sm"
                    >
                      {upgrading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
                      Upgrade to Enterprise
                    </Button>
                  </div>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Upgrade to Enterprise */}
          {user.subscription_status === 'active' && user.plan_tier === 'professional' && (
            <>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <ArrowUpRight className="h-4 w-4 text-primary" />
                  Upgrade to Enterprise
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Get unlimited properties, lower per-vendor rates ($3.50 vs $4.50), and Vendor-Paid billing options.
                </p>
                <p className="text-xs font-medium mb-3">
                  Prorated upgrade fee: <strong>$350</strong> (charged immediately)
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Next billing cycle and onwards: $599/month base fee
                </p>
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={handleUpgradeToEnterprise} 
                  disabled={upgrading}
                  data-testid="upgrade-to-enterprise-button"
                >
                  {upgrading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <ArrowUpRight className="mr-2 h-3 w-3" />}
                  {upgrading ? 'Upgrading...' : 'Upgrade to Enterprise'}
                </Button>
              </div>
              <Separator />
            </>
          )}

          {user.subscription_status === 'active' && user.plan_tier !== 'limited_demo' ? (
            <div className="space-y-3">
              <div className="flex gap-3">
                <Button 
                  onClick={handleManageBilling} 
                  disabled={loadingPortal}
                  data-testid="manage-billing-button"
                  className="flex-1"
                >
                  {loadingPortal ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                  {loadingPortal ? 'Opening...' : 'Manage Payment Method'}
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Opens Stripe's secure billing portal where you can update your payment method, view invoices, and manage your subscription.
              </p>

              <Separator />

              <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
                {canceledAtPeriodEnd ? (
                  <>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      Subscription Set to Cancel
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3" data-testid="subscription-canceled-notice">
                      Your subscription is scheduled to cancel at the end of the current billing period. You'll keep full access until then. You can reactivate anytime before it ends.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReactivateSubscription}
                      disabled={reactivating}
                      data-testid="reactivate-subscription-button"
                    >
                      {reactivating ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RotateCcw className="mr-2 h-3 w-3" />}
                      {reactivating ? 'Reactivating...' : 'Reactivate Subscription'}
                    </Button>
                  </>
                ) : (
                  <>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      Cancel Subscription
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Your account will remain active until the end of the current billing period. You can reactivate anytime.
                    </p>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setCancelOpen(true)}
                      disabled={canceling}
                      data-testid="cancel-subscription-button"
                    >
                      {canceling ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
                      {canceling ? 'Canceling...' : 'Cancel Subscription'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : user.plan_tier === 'limited_demo' ? (
            <div className="text-center py-6">
              <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-primary/10 mb-4">
                <Clock className="h-8 w-8 text-primary" />
              </div>
              {daysRemaining !== null && daysRemaining > 0 ? (
                <>
                  <p className="text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {daysRemaining} Day{daysRemaining !== 1 ? 's' : ''} Remaining
                  </p>
                  <p className="text-sm text-muted-foreground">
                    in your free trial
                  </p>
                </>
              ) : daysRemaining !== null ? (
                <>
                  <p className="text-2xl font-bold mb-1 text-destructive" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Trial Expired
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Please upgrade to continue
                  </p>
                </>
              ) : (
                <>
                  <p className="text-lg font-semibold mb-1">Free Trial Active</p>
                  <p className="text-sm text-muted-foreground">Loading trial info...</p>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-3">No active subscription</p>
              <Button onClick={() => navigate('/pricing')}>
                View Plans
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sign Out */}
      <Card className="rounded-xl shadow-[var(--shadow-soft)]">
        <CardContent className="p-4">
          <Button variant="outline" className="w-full" onClick={logout}>
            Sign Out
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="cancel-subscription-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              Your subscription will be scheduled to cancel at the end of the current billing period.
              You'll keep full access until then, and you can reactivate anytime before it ends.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-subscription-dismiss-button">Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              data-testid="cancel-subscription-confirm-button"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
